import {
  CalendarOutlined,
  ClockCircleOutlined,
  EditOutlined,
  EnvironmentOutlined,
  FireOutlined,
  MonitorOutlined,
  ReloadOutlined,
  UserOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Button, Col, Layout, Modal, PageHeader, Row, Space, Spin, Statistic, Typography } from 'antd';
import moment from 'moment';
import { useCallback, useContext, useEffect, useState } from 'react';
import { Risk } from '../../models';
import { apiClient, apiErrorMessage, hasPermission } from '../../utils';
import { UserContext } from '../contexts';
import { RiskEditPage } from './risk-edit-page';
import { RiskStatus } from './risk-status';
import { RiskActionsTable } from '../risk-actions/risk-actions-table';

export function RiskDetailPage(props: { riskId?: string; onSaved: () => void }) {
  const { Content } = Layout;
  const { Text } = Typography;
  const { user } = useContext(UserContext);

  const [risk, setRisk] = useState<Risk>();
  const [riskLoading, setRiskLoading] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [canEdit, setCanEdit] = useState(false);

  const baseApiUrl = (process.env.REACT_APP_ISG_BASE_API_URL || window.location.protocol + '//' + window.location.host) + '/api/';

  const loadRisk = useCallback(() => {
    setRiskLoading(true);
    apiClient
      .get(`risks/${props.riskId}`)
      .then(response => {
        setRisk(response.data);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setRiskLoading(false);
      });
  }, [props.riskId]);

  useEffect(() => {
    loadRisk();
  }, [loadRisk]);

  const onRefreshClick = () => {
    loadRisk();
  };

  useEffect(() => {
    if (hasPermission(user, 'risks.edit')) {
      setCanEdit(true);
      return;
    }
    if (risk?.smat) {
      if (
        hasPermission(user, 'smats.edit') ||
        (risk?.smat?.user?.id === user?.id && (risk?.smat?.status === 1 || risk?.smat?.status === 3))
      ) {
        setCanEdit(true);
      }
    }
  }, [user, risk?.smat]);

  const openEditModal = () => {
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
  };

  const onRiskEditSaved = () => {
    closeEditModal();
    props.onSaved();
  };

  const onRiskActionSaved = () => {
    props.onSaved();
    loadRisk();
  };

  return (
    <>
      <PageHeader
        title={risk?.riskNo}
        subTitle={<RiskStatus risk={risk} />}
        extra={[
          <Button key="refresh" onClick={onRefreshClick} icon={<ReloadOutlined />}>
            Yenile
          </Button>,
          <>
            {canEdit && (
              <Button key="edit" onClick={openEditModal} icon={<EditOutlined />}>
                Düzenle
              </Button>
            )}
          </>,
        ]}
      />
      <Content>
        <Spin spinning={riskLoading} style={{ paddingTop: '3rem', paddingBottom: '3rem' }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text strong>
              <EnvironmentOutlined /> {risk?.area ? `${risk?.area.code} - ${risk?.area.name}` : ''}
            </Text>
            <Space split="-">
              <Text>
                <UserOutlined /> {risk?.createdBy.fullNameId}
              </Text>
              <Text>
                <ClockCircleOutlined /> {moment(risk?.createdAt).format('DD.MM.yyyy HH:mm')}
              </Text>
            </Space>
            {risk?.smat && (
              <div>
                <Text>
                  <CalendarOutlined />
                  {` SMAT: ${risk?.smat?.smatNo} - ${risk?.smat?.week}. hafta`}
                </Text>
                <Text type="secondary" style={{ marginLeft: '1rem' }}>
                  ({risk?.smat?.startDate ? moment(risk?.smat?.startDate).format('DD.MM.yyyy') : null} -
                  {risk?.smat?.endDate ? moment(risk?.smat?.endDate).format('DD.MM.yyyy') : null})
                </Text>
              </div>
            )}
            <div>
              <Text style={{ display: 'inline-block', width: '8rem' }} type="secondary">
                Alan sorumlusu
              </Text>
              <Text>{risk?.area?.responsibleUser?.fullNameId}</Text>
            </div>
            <Row gutter={16} style={{ marginTop: '2rem', marginBottom: '2rem' }}>
              <Col span={6}>
                <Statistic title="Olasılık" value={risk?.beforeProbability} prefix={<MonitorOutlined />} />
              </Col>
              <Col span={6}>
                <Statistic title="Şiddet" value={risk?.beforeSeverity} prefix={<FireOutlined />} />
              </Col>
              <Col span={6}>
                <Statistic title="Risk puanı" value={risk?.beforeRiskGrade} prefix={<WarningOutlined />} />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Öncelik"
                  value={risk?.priority ? risk?.priority?.name : '-'}
                  valueStyle={{ color: '#' + risk?.priority?.color }}
                />
              </Col>
            </Row>
            <Row>
              <Col span={8}>Görüşülen kişi sayısı</Col>
              <Col span={16}>{risk?.interviewCount}</Col>
            </Row>
            <Row>
              <Col span={8}>Güvensiz davranış mı?</Col>
              <Col span={16}>{risk?.unsafeAction ? 'Evet' : 'Hayır'}</Col>
            </Row>
            {risk?.unsafeAction && (
              <Row>
                <Col span={8}>Güvensiz davranış isim</Col>
                <Col span={16}>{risk?.unsafeActionUser.fullNameId}</Col>
              </Row>
            )}
            <Row>
              <Col span={8}>Risk ve fırsatların tanımı</Col>
              <Col span={16}>{risk?.riskDescription}</Col>
            </Row>
            <Row>
              <Col span={8}>Karşı tedbir açıklaması / Fırsat</Col>
              <Col span={16}>{risk?.precautionDescription}</Col>
            </Row>
            <Row>
              <Col span={8}>Dokümanlar</Col>
              <Col span={16}>
                {risk?.documents?.length ? (
                  <Space direction="vertical">
                    {risk?.documents.map(d => (
                      <a href={`${baseApiUrl}risks/${risk.id}/documents/${d.id}`}>{d.path}</a>
                    ))}
                  </Space>
                ) : (
                  '-'
                )}
              </Col>
            </Row>
          </Space>
          <div style={{ marginTop: '4rem' }}>
            <RiskActionsTable risk={risk} onSaved={onRiskActionSaved} />
          </div>
        </Spin>
      </Content>
      <Modal title="Risk bildirimi" visible={editModalOpen} footer={null} onCancel={closeEditModal} destroyOnClose={true}>
        <RiskEditPage risk={risk} onSaved={onRiskEditSaved} />
      </Modal>
    </>
  );
}
